import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useManualQuery } from "graphql-hooks";
import {
  Avatar,
  Box,
  Button,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PublicPageLayout from "./PublicPageLayout";
import ProductCard from "../components/ProductCard";
import FloatingMenu from "../components/FloatingMenu";
import AlertPopup from "../components/AlertPopup";
import EmptyState from "../components/EmptyState";
import dayjs from "dayjs";
import { isValidUUIDv4 } from "../hooks/uuid";

import useIsTablet from "../hooks/useIsTablet";
import useIsMobile from "../hooks/useIsMobile";

// Import Avatar images
import Avatar1 from "../assets/SearchResults/Avatars/James_Anderson.png";
import Avatar2 from "../assets/SearchResults/Avatars/David_Miller.png";
import Avatar3 from "../assets/SearchResults/Avatars/Robert_Thompson.png";
import Avatar4 from "../assets/SearchResults/Avatars/Mary_Robinson.png";
import Avatar5 from "../assets/SearchResults/Avatars/Michael_Harris.png";
import Avatar6 from "../assets/SearchResults/Avatars/Michael_Harris_2.png";
import Avatar7 from "../assets/SearchResults/Avatars/Sarah_White.png";
import Avatar8 from "../assets/SearchResults/Avatars/Jennifer_Taylor.png";
import Avatar9 from "../assets/SearchResults/Avatars/Linda_Martinez.png";
import placeholderImage from "../assets/r3play-logo-sml.png";

import { VIDEOS_BY_USER_UUID } from "../graphQL/video_qry";
import {
  CREATE_COLLECTION_MUTATION,
  DELETE_COLLECTION_MUTATION,
} from "../graphQL/collection_qry";

const sortList = ["Ascending", "Descending"];

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

const PublicVideos = () => {
  const { userUuid } = useParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("Ascending");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFollowing, setIsFollowing] = useState(false);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    type: "",
    productId: null,
    productTitle: "",
  });
  const [warningPopup, setWarningPopup] = useState({
    open: false,
  });
  const [isLoaded, setIsLoaded] = useState(false);

  function timeAgo(dateString) {
    const date = new Date(dateString);
    const now = new Date();

    const diffMs = now - date;
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) return "Just now";
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (weeks < 5) return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
    if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
    return `${years} year${years > 1 ? "s" : ""} ago`;
  }

  function formatMonthYear(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
    });
  }

  function formatNumber(num) {
    if (num === null || num === undefined) return "0";
    if (num < 1000) return num.toString();
    if (num < 1_000_000)
      return (num / 1000).toFixed(num >= 10000 ? 0 : 1) + "K";
    if (num < 1_000_000_000) return (num / 1_000_000).toFixed(1) + "M";
    return (num / 1_000_000_000).toFixed(1) + "B";
  }

  const getVideoThumbnail = (videoUrl) => {
    if (!videoUrl) return "";

    // YouTube
    if (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be")) {
      let videoId = "";
      if (videoUrl.includes("youtu.be/")) {
        videoId = videoUrl.split("youtu.be/")[1].split("?")[0];
      } else if (videoUrl.includes("youtube.com")) {
        const urlParams = new URLSearchParams(videoUrl.split("?")[1]);
        videoId = urlParams.get("v");
      }
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }

    // Vimeo
    if (videoUrl.includes("vimeo.com")) {
      const videoId = videoUrl.split("/").pop().split("?")[0];
      return `https://vumbnail.com/${videoId}.jpg`;
    }

    // Dailymotion
    if (videoUrl.includes("dailymotion.com") || videoUrl.includes("dai.ly")) {
      let videoId = "";
      if (videoUrl.includes("dai.ly/")) {
        videoId = videoUrl.split("dai.ly/")[1].split("?")[0];
      } else {
        videoId = videoUrl.split("/video/")[1].split("?")[0];
      }
      return `https://www.dailymotion.com/thumbnail/video/${videoId}`;
    }

    // Default fallback - return empty or a placeholder
    return "";
  };

  //remove from collection by sami
  const [confirmPopup, setConfirmPopup] = useState({
    open: false,
    productId: 0,
    title: "",
  });

  //add to collection by sami
  const [successPopup, setSuccessPopup] = useState({
    open: false,
    productId: 0,
    title: "",
  });

  //Sami
  const [createCollection] = useMutation(CREATE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [deleteCollection] = useMutation(DELETE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [getVideosData] = useManualQuery(VIDEOS_BY_USER_UUID, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    setVideos(prev =>
      prev.map(video => ({
        ...video,
        userId: {
          ...video.userId,
          isFollowing,
        },
      }))
    );
  }, [isFollowing]);

  const setVideoList = async () => {
    if (!userUuid) return;
    if (isValidUUIDv4(userUuid)) {
      const {
        loading: loadingVideoListData,
        error: errorVideoListData,
        data: videoListData,
      } = await getVideosData({
        variables: {
          userUuid: userUuid,
          status: "Published",
        },
      });
      if (
        !loadingVideoListData &&
        videoListData &&
        videoListData.videosByUseruuid
      ) {
        console.log(videoListData);
        const firstVideoUser =
          videoListData.videosByUseruuid[0]?.userId;

        if (firstVideoUser) {
          setIsFollowing(firstVideoUser.isLoggedInUserFollowing);
        }
        setVideos(
          videoListData.videosByUseruuid.map((video, index) => {
            const randIdx = index % 9;
            return {
              id: video.videoId,
              uuid: video.uuid,
              title: video.title,
              content: video.content,
              contentType: "Videos",
              shareUrl: `${window.location.origin}/video/${video.uuid}`,
              likes: video.likeNo,
              isLiked: video.isLiked,
              shares: video.shareNo,
              videoUrl: video.videoUrl,
              img: getVideoThumbnail(video.videoUrl) || placeholderImage,
              userId: {
                userId: video.userId.id,
                userUuid: video.userId.userUuid,
                firstName: video.userId.firstName,
                lastName: video.userId.lastName,
                username: video.userId.username,
                avatarFileName: video.userId.avatarFileName,
                isFollowing: video.userId.isLoggedInUserFollowing,
                listings: video.userId.listings,
                soldToys: video.userId.soldToys,
                ratingNo: video.userId.ratingNo,
                ratingCount: video.userId.ratingCount,
                dateJoined: video.userId.dateJoined,
              },
              isInCollection: video.isInCollection,
              likedUsers: video.videoLikes.map((videoLike, index) => {
                return {
                  id: videoLike.videoLikeId,
                  username: videoLike.userId.username,
                  avatar: videoLike.userId.avatarFileName,
                  isFollowing: videoLike.userId.isLoggedInUserFollowing,
                };
              }),
              commentNo: video.commentNo,
              comments: video.videoComments,
            };
          })
        );
        setIsLoaded(true);
      } else {
        console.log("errorCurrVideoData: ", errorVideoListData);
      }
    } else {
      setWarningPopup({
        open: true,
        title: "This URL is not valid.",
      });
    }
  };

  useEffect(() => {
    (async () => setVideoList())();
  }, [userUuid]);

  const isTablet = useIsTablet(1360); // Use 1360px breakpoint for tablet size
  const isMobile = useIsMobile(856); // Use 856px breakpoint for mobile size

  let loggedUserName = localStorage.getItem("userName")
    ? localStorage.getItem("userName")
    : null;

  // Set rows per page based on screen size
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 833;
      setRowsPerPage(isMobile ? 5 : 10);
      setPage(0); // Reset to first page when changing rows per page
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Pagination handlers
  //const handleChangePage = (event, newPage) => {
  //setPage(newPage);
  //};

  //const handleChangeRowsPerPage = (event) => {
  //setRowsPerPage(parseInt(event.target.value));
  //setPage(0);
  //};

  // Filter and sort Videos
  const getFilteredAndSortedVideos = () => {
    let filtered = videos.filter((s) =>
      s.title.toLowerCase().includes(search.toLowerCase())
    );

    // Sort by title
    filtered.sort((a, b) => {
      const titleA = a.title.toLowerCase();
      const titleB = b.title.toLowerCase();

      if (sortBy === "Ascending") {
        return titleA.localeCompare(titleB);
      } else {
        return titleB.localeCompare(titleA);
      }
    });

    return filtered;
  };

  const filteredVideos = getFilteredAndSortedVideos();

  // Get current page data from filtered and sorted results
  const paginatedVideos = filteredVideos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handle bookmark click to show confirmation popup
  const handleBookmarkClick = (productId, isInCollection) => {
    const product = videos.find((p) => p.id === productId);
    if (isInCollection) {
      // Show removal confirmation
      setConfirmPopup({
        open: true,
        productId: productId,
        title: product ? product.title : "",
      });
    } else {
      // Show add alert
      setSuccessPopup({
        open: true,
        productId: productId,
        title: product ? product.title : "",
      });
    }
  };

  const handleConfirmAdd = async () => {
    await createCollection({
      variables: {
        objectId: parseInt(successPopup.productId),
        contentType: "video",
      },
    });
    setSuccessPopup({ open: false, productId: 0, title: "" });

    setVideos((prev) =>
      prev.map((product) =>
        product.id === successPopup.productId
          ? { ...product, isInCollection: !product.isInCollection }
          : product
      )
    );
  };

  const handleConfirmRemove = async () => {
    await deleteCollection({
      variables: {
        objectId: parseInt(confirmPopup.productId),
        contentType: "video",
      },
    });

    setVideos((prev) =>
      prev.map((product) =>
        product.id === confirmPopup.productId
          ? { ...product, isInCollection: !product.isInCollection }
          : product
      )
    );
    setConfirmPopup({ open: false, productId: 0, title: "" });
  };

  // Handle cancel action
  const handleCancelAction = () => {
    setConfirmPopup({ open: false, productId: 0, title: "" });
    setSuccessPopup({ open: false, productId: 0, title: "" });
  };

  const handleUserProfileNavigation = (user) => {
    // Navigate to user's public homepage
    navigate(`/user/${user.userUuid}/profile`);
  };

  const handleUserFollowUpdate = (newIsFollowing) => {
    setIsFollowing(newIsFollowing);

    setVideos(prev =>
      prev.map(video => ({
        ...video,
        userId: {
          ...video.userId,
          isFollowing: newIsFollowing,
        },
      }))
    );
  };

  // Handle share click to increment share count
  const handleShareClick = (productId) => {
    setVideos((prev) =>
      prev.map((product) =>
        product.id === productId
          ? { ...product, shares: (product.shares || 0) + 1 }
          : product
      )
    );
  };

  return (
    <PublicPageLayout
      activeMenuItem="videos"
      userUuid={userUuid}
      isFollowing={isFollowing}
      onFollowChange={setIsFollowing}
    >      {/* Header */}
      <Box p={2}>
        <Typography variant="h6">Videos</Typography>
        <Typography variant="body2" color="text.secondary">
          Discover moments captured and shared through videos.
        </Typography>
      </Box>

      {/* Divider under header */}
      <Divider sx={{ mb: 2 }} />

      {/* Search and Sort Section - Commented by RR 2025/12/13*/}
      <Box pr={2} pl={2}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            bgcolor: "#F5F5F5", // Gray box around
            p: "16px",
            borderRadius: 2,
            "@media (max-width: 833px)": {
              flexDirection: "column",
              alignItems: "stretch",
            },
          }}
        >
          {/* Search box */}
          <TextField
            fullWidth
            placeholder="Search videos..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: "#fff",
              flexGrow: 1,
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d0d0d0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                  borderWidth: "1px",
                },
              },
            }}
          />

          {/* Sort dropdown */}
          <FormControl
            size="small"
            sx={{
              minWidth: "200px",
              "@media (max-width: 833px)": {
                width: "100%",
                mt: 1, // Add margin top on mobile
              },
              bgcolor: "#fff",
              borderRadius: 1,
            }}
          >
            <InputLabel
              sx={{
                color: "#707070",
                "&.Mui-focused": { color: "#707070" },
              }}
            >
              Sort by
            </InputLabel>
            <Select
              label="Sort by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              displayEmpty
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d0d0d0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d0d0d0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d0d0d0",
                  borderWidth: "1px",
                },
              }}
            >
              {sortList.map((srt) => (
                <MenuItem key={srt} value={srt}>
                  {srt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Divider under search section */}
      <Divider />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent:
            isLoaded && filteredVideos.length === 0 ? "center" : "flex-start",
          gap: "24px",
          width: "912px",
          m: 2,
          pt: "1px",
          "@media (max-width: 1360px)": {
            width: "626px",
            gap: "16px",
            justifyContent: "center",
            mb: 2,
            mx: "auto",
          },
          "@media (max-width: 856px)": {
            width: "361px",
            justifyContent: "center",
            gap: 0,
            mx: "auto",
          },
        }}
      >
        {!isLoaded ? (
          <Box p={4} textAlign="center">
            Loading...
          </Box>
        ) : isLoaded && filteredVideos.length === 0 ? (
          <EmptyState
            type="videos"
            title="No Videos Available"
            description="No videos are available right now. Please check back later."
          />
        ) : (
          <>
            {filteredVideos.map((p, index) => {
              return (
                <Box
                  key={p.id}
                  sx={{
                    width: "288px",
                    position: "relative",
                    "@media (max-width: 856px)": {
                      mb: "24px",
                    },
                  }}
                >
                  <ProductCard
                    p={p}
                    onBookmarkClick={handleBookmarkClick}
                    onUserProfileNavigation={handleUserProfileNavigation}
                    onFollowToggle={handleUserFollowUpdate}
                    onShareClick={handleShareClick}
                  />
                </Box>
              );
            })}
          </>
        )}
      </Box>

      {/* Alert Popup for add collection actions */}
      <AlertPopup
        open={successPopup.open}
        title={`Item "${successPopup.title}" is added to your collection.`}
        cancelText=""
        confirmText="OK"
        onConfirm={handleConfirmAdd}
        onClose={() =>
          setSuccessPopup({ open: false, productId: 0, title: "" })
        }
      />

      {/* Alert Popup for remove collection actions */}
      <AlertPopup
        open={confirmPopup.open}
        title={`Are you sure you want to remove "${confirmPopup.title}" from your collection?`}
        confirmText="Yes, Remove"
        cancelText="Cancel"
        onConfirm={handleConfirmRemove}
        onCancel={() =>
          setConfirmPopup({ open: false, productId: 0, title: "" })
        }
        onClose={() =>
          setConfirmPopup({ open: false, productId: 0, title: "" })
        }
      />

      <AlertPopup
        open={warningPopup.open}
        title={warningPopup.title}
        message=""
        confirmText="OK"
        cancelText=""
        onConfirm={() => setWarningPopup(false)}
        onClose={() => setWarningPopup(false)}
      />

      <FloatingMenu left={floatMenuLeftMargin} />
    </PublicPageLayout>
  );
};

export default PublicVideos;
